import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, List, Plus, ArrowLeft, ChevronLeft, ChevronRight, MapPin, Clock, Users } from "lucide-react";
import { API, BOOKING_SERVICES } from "../data/site";
import { JobModal, JOB_STATUSES, statusLabel } from "../components/JobModal";
import { TeamModal } from "../components/TeamModal";

const STATUS_STYLES = {
    quote: "bg-ochre/25 text-clay",
    booked: "bg-forest/10 text-forest",
    in_progress: "bg-forest text-bone",
    completed: "bg-forest-leaf/15 text-forest-leaf",
    cancelled: "bg-stone-200 text-stone-500 line-through",
    paid: "bg-ochre text-forest-night",
};

const selectCls = "rounded-full border border-forest/20 bg-bone-card px-4 py-2 text-xs font-bold text-forest outline-none";

export default function AdminScheduler() {
    const token = localStorage.getItem("lb_admin") || "";
    const role = localStorage.getItem("lb_role") || "admin";
    const workerName = localStorage.getItem("lb_worker") || "";
    const isAdmin = role === "admin";
    const [jobs, setJobs] = useState(null);
    const [team, setTeam] = useState([]);
    const [teamDetails, setTeamDetails] = useState([]);
    const [teamOpen, setTeamOpen] = useState(false);
    const [view, setView] = useState("calendar");
    const [cursor, setCursor] = useState(() => new Date());
    const [filters, setFilters] = useState({ status: "", service: "", worker: "" });
    const [modal, setModal] = useState(null); // null | "new" | job object

    const load = useCallback(async () => {
        if (!token) return;
        try {
            const [{ data: j }, { data: t }] = await Promise.all([
                axios.get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/team`, { headers: { Authorization: `Bearer ${token}` } }),
            ]);
            setJobs(j.jobs);
            setTeam(t.workers);
            setTeamDetails(t.details || []);
        } catch {
            toast.error("Could not load jobs — log in again from the bookings page.");
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => (jobs || []).filter((j) =>
        (!filters.status || j.job_status === filters.status) &&
        (!filters.service || j.services.includes(filters.service)) &&
        (!filters.worker || j.workers.includes(filters.worker))
    ), [jobs, filters]);

    const byDate = useMemo(() => {
        const map = {};
        for (const j of filtered) (map[j.date] = map[j.date] || []).push(j);
        return map;
    }, [filtered]);

    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstDay = new Date(year, month, 1);
    const offset = (firstDay.getDay() + 6) % 7; // Monday start
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
    const todayStr = new Date().toISOString().split("T")[0];
    const monthName = cursor.toLocaleDateString("en-AU", { month: "long", year: "numeric" });

    const onSaved = (saved) => {
        setJobs((prev) => {
            const exists = prev?.some((j) => j.id === saved.id);
            const next = exists ? prev.map((j) => (j.id === saved.id ? saved : j)) : [...(prev || []), saved];
            return next;
        });
        for (const w of saved.workers) if (!team.includes(w)) setTeam((t) => [...t, w].sort());
        setModal(null);
    };

    const onDeleted = (id) => {
        setJobs((prev) => prev.filter((j) => j.id !== id));
        setModal(null);
    };

    if (!token) {
        return (
            <main className="flex min-h-screen items-center justify-center bg-bone px-6 pt-16">
                <div className="text-center">
                    <p className="text-sm text-moss">Please log in first.</p>
                    <Link to="/admin" data-testid="scheduler-login-link" className="mt-4 inline-block rounded-full bg-forest px-6 py-3 text-sm font-bold text-bone">
                        Go to Admin Login
                    </Link>
                </div>
            </main>
        );
    }

    return (
        <main data-testid="scheduler-page" className="min-h-screen bg-bone px-6 pb-20 pt-24">
            <div className="mx-auto max-w-6xl">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <Link to="/admin" data-testid="scheduler-back-link" className="flex items-center gap-1.5 text-xs font-bold text-forest hover:text-forest-leaf">
                            <ArrowLeft size={13} /> Bookings
                        </Link>
                        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-ink">Job Scheduler</h1>
                    </div>
                    {isAdmin && (
                        <div className="flex gap-3">
                            <button onClick={() => setTeamOpen(true)} data-testid="team-manage-button"
                                className="flex items-center gap-2 rounded-full border border-forest/20 px-5 py-3 text-sm font-bold text-forest transition-colors hover:bg-bone-subtle">
                                <Users size={15} /> Team
                            </button>
                            <button onClick={() => setModal("new")} data-testid="add-job-button"
                                className="flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-bold text-bone transition-colors hover:bg-forest-deep">
                                <Plus size={16} /> Add New Job
                            </button>
                        </div>
                    )}
                </div>

                {!isAdmin && (
                    <div data-testid="employee-banner" className="mt-6 rounded-2xl border border-ochre/40 bg-ochre/10 px-5 py-3 text-sm font-semibold text-ink">
                        Logged in as {workerName} — view-only access. You can see all job details but can&apos;t change anything.
                    </div>
                )}

                {!isAdmin && workerName && jobs && (() => {
                    const mine = jobs.filter((j) => j.workers.includes(workerName) && j.date >= todayStr);
                    return (
                        <div data-testid="my-jobs-section" className="mt-6">
                            <p className="eyebrow text-forest-leaf">Your upcoming jobs</p>
                            {mine.length === 0 ? (
                                <p data-testid="my-jobs-empty" className="mt-3 rounded-2xl border border-dashed border-forest/25 p-6 text-center text-sm text-moss">
                                    No upcoming jobs assigned to you yet.
                                </p>
                            ) : (
                                <div className="mt-3 space-y-3">
                                    {mine.slice(0, 5).map((j) => (
                                        <button key={j.id} onClick={() => setModal(j)} data-testid={`my-job-${j.id}`}
                                            className="flex w-full flex-wrap items-center gap-x-4 gap-y-1.5 rounded-2xl border border-ochre/40 bg-ochre/10 p-4 text-left">
                                            <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-clay">
                                                {j.date}{j.start_time && ` · ${j.start_time}`}
                                            </span>
                                            <span className="font-display text-base font-bold text-ink">{j.customer_name}</span>
                                            <span className="flex items-center gap-1 text-xs text-moss"><MapPin size={11} /> {j.address}</span>
                                            <span className="ml-auto flex flex-wrap gap-1.5">
                                                {j.services.map((s) => <span key={s} className="rounded-full bg-forest/10 px-2.5 py-1 text-[10px] font-bold text-forest">{s}</span>)}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })()}

                <div className="mt-8 flex flex-wrap items-center gap-3">
                    <select data-testid="filter-status" className={selectCls} value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                        <option value="">All statuses</option>
                        {JOB_STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                    </select>
                    <select data-testid="filter-service" className={selectCls} value={filters.service}
                        onChange={(e) => setFilters({ ...filters, service: e.target.value })}>
                        <option value="">All services</option>
                        {BOOKING_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select data-testid="filter-worker" className={selectCls} value={filters.worker}
                        onChange={(e) => setFilters({ ...filters, worker: e.target.value })}>
                        <option value="">All team members</option>
                        {team.map((w) => <option key={w} value={w}>{w}</option>)}
                    </select>
                    <div className="ml-auto flex gap-2">
                        <button onClick={() => setView("calendar")} data-testid="view-calendar-button" aria-pressed={view === "calendar"}
                            className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors ${view === "calendar" ? "bg-forest text-bone" : "border border-forest/20 text-forest hover:bg-bone-subtle"}`}>
                            <CalendarDays size={13} /> Calendar
                        </button>
                        <button onClick={() => setView("list")} data-testid="view-list-button" aria-pressed={view === "list"}
                            className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors ${view === "list" ? "bg-forest text-bone" : "border border-forest/20 text-forest hover:bg-bone-subtle"}`}>
                            <List size={13} /> Schedule
                        </button>
                    </div>
                </div>

                {!jobs ? (
                    <p className="mt-16 text-center text-sm text-moss">Loading jobs...</p>
                ) : view === "calendar" ? (
                    <div className="mt-6 rounded-3xl border border-forest/10 bg-bone-card p-4 sm:p-6">
                        <div className="flex items-center justify-between">
                            <button onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="calendar-prev" aria-label="Previous month"
                                className="flex h-9 w-9 items-center justify-center rounded-full border border-forest/15 text-forest hover:bg-bone-subtle">
                                <ChevronLeft size={16} />
                            </button>
                            <p data-testid="calendar-month" className="font-display text-lg font-bold text-ink">{monthName}</p>
                            <button onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="calendar-next" aria-label="Next month"
                                className="flex h-9 w-9 items-center justify-center rounded-full border border-forest/15 text-forest hover:bg-bone-subtle">
                                <ChevronRight size={16} />
                            </button>
                        </div>
                        <div className="mt-4 grid grid-cols-7 gap-1">
                            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                                <div key={d} className="py-1 text-center font-mono text-[10px] font-semibold uppercase tracking-wider text-moss">{d}</div>
                            ))}
                            {cells.map((day, i) => {
                                if (!day) return <div key={`e${i}`} />;
                                const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                                const dayJobs = byDate[dateStr] || [];
                                return (
                                    <div key={dateStr} data-testid={`calendar-day-${dateStr}`}
                                        className={`min-h-[72px] rounded-xl border p-1.5 sm:min-h-[96px] ${dateStr === todayStr ? "border-ochre bg-ochre/10" : "border-forest/10 bg-bone"}`}>
                                        <p className={`text-right font-mono text-[10px] ${dateStr === todayStr ? "font-bold text-clay" : "text-moss"}`}>{day}</p>
                                        <div className="mt-1 space-y-1">
                                            {dayJobs.slice(0, 3).map((j) => (
                                                <button key={j.id} onClick={() => setModal(j)} data-testid={`calendar-job-${j.id}`}
                                                    className={`block w-full truncate rounded-md px-1.5 py-1 text-left text-[10px] font-bold ${STATUS_STYLES[j.job_status] || "bg-forest/10 text-forest"}`}>
                                                    {j.start_time && `${j.start_time} `}{j.customer_name}
                                                </button>
                                            ))}
                                            {dayJobs.length > 3 && <p className="px-1 text-[9px] font-semibold text-moss">+{dayJobs.length - 3} more</p>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {filtered.length === 0 && (
                            <p data-testid="scheduler-empty" className="py-10 text-center text-sm text-moss">
                                No jobs match these filters — hit Add New Job to schedule your first one.
                            </p>
                        )}
                    </div>
                ) : (
                    <div data-testid="schedule-list" className="mt-6 space-y-3">
                        {filtered.length === 0 ? (
                            <p data-testid="scheduler-empty" className="rounded-3xl border border-dashed border-forest/25 p-16 text-center text-sm text-moss">
                                No jobs match these filters — hit Add New Job to schedule one.
                            </p>
                        ) : filtered.map((j) => (
                            <button key={j.id} onClick={() => setModal(j)} data-testid={`schedule-job-${j.id}`}
                                className="flex w-full flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-forest/10 bg-bone-card p-5 text-left transition-transform duration-200 hover:-translate-y-0.5">
                                <div className="min-w-[110px]">
                                    <p className="font-mono text-[10px] uppercase tracking-wider text-moss">{j.date}</p>
                                    <p className="mt-0.5 flex items-center gap-1 text-sm font-bold text-ink"><Clock size={12} /> {j.start_time || "Any time"}</p>
                                </div>
                                <div className="min-w-[160px] flex-1">
                                    <p className="font-display text-base font-bold text-ink">{j.customer_name}</p>
                                    <p className="flex items-center gap-1 text-xs text-moss"><MapPin size={11} /> {j.address || "Address TBC"}</p>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {j.services.map((s) => <span key={s} className="rounded-full bg-forest/10 px-2.5 py-1 text-[10px] font-bold text-forest">{s}</span>)}
                                </div>
                                {j.workers.length > 0 && (
                                    <p className="flex items-center gap-1.5 text-xs font-semibold text-moss"><Users size={12} /> {j.workers.join(", ")}</p>
                                )}
                                {j.quote_price && <p className="font-display text-base font-bold text-forest">${j.quote_price}</p>}
                                <span className={`rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] ${STATUS_STYLES[j.job_status]}`}>
                                    {statusLabel(j.job_status)}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {modal && (
                <JobModal
                    job={modal === "new" ? null : modal}
                    team={team}
                    token={token}
                    readOnly={!isAdmin}
                    onClose={() => setModal(null)}
                    onSaved={onSaved}
                    onDeleted={onDeleted}
                />
            )}
            {teamOpen && isAdmin && (
                <TeamModal details={teamDetails} token={token} onClose={() => setTeamOpen(false)} onChanged={load} />
            )}
        </main>
    );
}
